/**
 * Presentation layer for the MoneyInput component.
 *
 * Renders an inline editor: € prefix, numeric text input,
 * and M/K segmented toggle. Saves on Enter, cancels on Escape.
 * Supports "compact" (table cells) and "expanded" (modals) variants.
 * @module components/MoneyInput/MoneyInputView
 */

import React, { memo } from "react";
import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";

import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import type { MoneyInputVariant } from "./MoneyInputContainer";

/** Active toggle classes — blue background matching project CTA pattern. */
const ACTIVE_BG_CLASS = "bg-accent-primary";
const ACTIVE_TEXT_CLASS = "text-content-on-accent";

/** Unit toggle options */
export type MoneyUnit = "M" | "K";

/** Props for {@link MoneyInputView} */
export interface MoneyInputViewProps {
  /** Current numeric display value (e.g. "5" for 5M or "500" for 500K) */
  readonly displayValue: string;
  /** Current unit toggle state */
  readonly unit: MoneyUnit;
  /** Called when the numeric input changes */
  readonly onChangeText: (text: string) => void;
  /** Called when M is pressed */
  readonly onSelectM: () => void;
  /** Called when K is pressed */
  readonly onSelectK: () => void;
  /** Called on Enter to save */
  readonly onSubmit: () => void;
  /** Called when the TextInput loses focus — saves on blur */
  readonly onBlur?: () => void;
  /** Called on Escape to discard */
  readonly onCancel: () => void;
  /** Called on key press for Enter/Escape handling */
  readonly onKeyPress: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => void;
  /** Ref to the TextInput for programmatic focus */
  readonly inputRef?: React.RefObject<React.ComponentRef<typeof TextInput>>;
  /** Whether the input is disabled */
  readonly isDisabled?: boolean;
  /** Placeholder text for the input */
  readonly placeholder?: string;
  /** Size variant: "compact" for table cells, "expanded" for modals */
  readonly variant?: MoneyInputVariant;
}

/**
 * Renders a MoneyInput with € prefix, numeric field, and M/K toggle.
 * Saves on Enter, cancels on Escape.
 * @param props - Component props
 * @param props.displayValue - Current display value string
 * @param props.unit - Current unit (M or K)
 * @param props.onChangeText - Text change handler
 * @param props.onSelectM - Selects M unit
 * @param props.onSelectK - Selects K unit
 * @param props.onSubmit - Saves on Enter/blur
 * @param props.onCancel - Discards on Escape
 * @param props.onKeyPress - Key press handler
 * @param props.inputRef - Ref to the TextInput for programmatic focus
 * @param props.isDisabled - Whether the input is disabled
 * @param props.placeholder - Placeholder text
 * @param props.variant - Size variant
 */
/** Lookup tables for variant-specific classes */
const VARIANT_GAP: Record<MoneyInputVariant, string> = {
  compact: "gap-1",
  expanded: "gap-2",
};
const VARIANT_TOGGLE_TEXT: Record<MoneyInputVariant, string> = {
  compact: "text-2xs",
  expanded: "text-caption",
};
const VARIANT_TOGGLE_PAD: Record<MoneyInputVariant, string> = {
  compact: "px-1 py-micro",
  expanded: "px-3",
};
const VARIANT_TOGGLE_CONTAINER: Record<MoneyInputVariant, string> = {
  compact: "overflow-hidden rounded-sm bg-surface-subtle p-px",
  expanded: "h-9 overflow-hidden rounded-md border border-outline-default",
};
const VARIANT_INPUT_CLASS: Record<MoneyInputVariant, string> = {
  compact:
    "h-6 w-16 rounded-sm border border-outline-default px-1 py-micro text-caption text-content-primary",
  // `min-w-0` is the containment half of SE-7731 — a `flex-1` `<input>`
  // cannot shrink below its UA min-content width without it, so the expanded
  // field would paint outside its row rather than narrow with it.
  expanded:
    "h-9 min-w-0 flex-1 rounded-md border border-outline-default px-3 py-2 text-body text-content-primary web:outline-none web:focus:border-accent-primary",
};
const VARIANT_M_LABEL: Record<MoneyInputVariant, string> = {
  compact: "M",
  expanded: "Millions",
};
const VARIANT_K_LABEL: Record<MoneyInputVariant, string> = {
  compact: "K",
  expanded: "Thousands",
};

/**
 * Renders a MoneyInput with € prefix, numeric field, and M/K toggle.
 * Saves on Enter, cancels on Escape.
 * @param root0 - Component props
 * @param root0.displayValue - Current display value string
 * @param root0.unit - Current unit (M or K)
 * @param root0.onChangeText - Text change handler
 * @param root0.onSelectM - Selects M unit
 * @param root0.onSelectK - Selects K unit
 * @param root0.onSubmit - Saves on Enter
 * @param root0.onBlur - Saves on blur (focus loss)
 * @param root0.onKeyPress - Key press handler
 * @param root0.inputRef - Ref to the TextInput for programmatic focus
 * @param root0.isDisabled - Whether the input is disabled
 * @param root0.placeholder - Placeholder text
 * @param root0.variant - Size variant
 */
const MoneyInputView = ({
  displayValue,
  unit,
  onChangeText,
  onSelectM,
  onSelectK,
  onSubmit,
  onBlur,
  onKeyPress,
  inputRef,
  isDisabled = false,
  placeholder = "0",
  variant = "compact",
}: MoneyInputViewProps) => (
  <Stack
    direction="horizontal"
    className={`items-center ${VARIANT_GAP[variant]}`}
  >
    <TextInput
      ref={inputRef}
      testID="money-input-field"
      className={VARIANT_INPUT_CLASS[variant]}
      value={`€${displayValue}`}
      onChangeText={onChangeText}
      onKeyPress={onKeyPress}
      onSubmitEditing={onSubmit}
      onBlur={onBlur}
      placeholder={`€${placeholder}`}
      placeholderTextColor="#a3a3a3"
      keyboardType="decimal-pad"
      editable={!isDisabled}
      autoFocus
    />
    <Stack direction="horizontal" className={VARIANT_TOGGLE_CONTAINER[variant]}>
      <Pressable
        testID="money-unit-m"
        onPress={onSelectM}
        className={`${VARIANT_TOGGLE_PAD[variant]} items-center justify-center rounded-l-none ${unit === "M" ? ACTIVE_BG_CLASS : ""}`}
        disabled={isDisabled}
      >
        <Text
          variant="unstyled"
          className={`font-heading ${VARIANT_TOGGLE_TEXT[variant]} ${unit === "M" ? ACTIVE_TEXT_CLASS : "text-content-subtle"}`}
        >
          {VARIANT_M_LABEL[variant]}
        </Text>
      </Pressable>
      <Pressable
        testID="money-unit-k"
        onPress={onSelectK}
        className={`${VARIANT_TOGGLE_PAD[variant]} items-center justify-center rounded-r-none ${unit === "K" ? ACTIVE_BG_CLASS : ""}`}
        disabled={isDisabled}
      >
        <Text
          variant="unstyled"
          className={`font-heading ${VARIANT_TOGGLE_TEXT[variant]} ${unit === "K" ? ACTIVE_TEXT_CLASS : "text-content-subtle"}`}
        >
          {VARIANT_K_LABEL[variant]}
        </Text>
      </Pressable>
    </Stack>
  </Stack>
);

MoneyInputView.displayName = "MoneyInputView";

export default memo(MoneyInputView);
